import InspectionDetailModal from '@components/Viewer/UserInterface/InspectionDetail/InspectionDetail';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { type Inspection } from '@lib/spproject/types';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Card, CardContent, IconButton, InputBase, Pagination, Paper, Typography } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { useLazyGetInspectionFileQuery } from '@store/api/inspectionEndpoints';
import { useGetProjectQuery } from '@store/api/projectEndpoints';
import { useAppDispatch, useLoading } from '@store/hooks';
import { setInspection, setSelectedPOI, setStepIndex } from '@store/reducers/viewerSlice';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import InspectionAccordion from './InspectionAccordion';

interface InspectionsTabProps {
  projectId: string;
  inspections: IInspection[];
}

const ITEMS_PER_PAGE = 10;

const InspectionsTab = ({ projectId, inspections }: InspectionsTabProps) => {
  const [expandedInspections, setExpandedInspections] = useState<Set<string>>(new Set());
  const [cachedInspections, setCachedInspections] = useState<Map<string, SPInspection>>(
    () => new Map()
  );
  const [fetchInspection] = useLazyGetInspectionFileQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { show, hide } = useLoading();
  const { t } = useLocalization();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { data: project } = useGetProjectQuery(projectId);

  const [loadingState, setLoadingState] = useState({
    inspection: false,
    poi: false,
    fetching: false
  });

  const isLoading = Object.values(loadingState).some(Boolean);

  const { inspectionId, poiId, stepIndex } = useParams();
  const componentId = useId();

  const query = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);

  const ensureInspectionCached = useCallback(
    async (id: string): Promise<SPInspection> => {
      const cached = cachedInspections.get(id);
      if (cached) return cached;

      const file = await fetchInspection({ projectId, inspectionId: id }).unwrap();
      const sp = await SPInspection.loadAsync(file);

      setCachedInspections(prev => {
        const next = new Map(prev);
        next.set(id, sp);
        return next;
      });

      return sp;
    },
    [cachedInspections, fetchInspection, projectId]
  );

  useEffect(() => {
    const initPOI = async () => {
      if (!project || !inspections.length || !inspectionId || !poiId) return;

      setLoadingState(prev => ({ ...prev, poi: true }));
      try {
        const sp = await ensureInspectionCached(inspectionId);
        dispatch(setInspection(sp));
        const poi = sp.inspection.pointOfInterests.find(p => p.id === poiId);
        if (!poi) throw new Error('POI not found');
        dispatch(setSelectedPOI(poi));
        const sIndex = Number(stepIndex);
        dispatch(setStepIndex(isNaN(sIndex) ? 0 : sIndex));
      } catch (error) {
        console.error('Failed to load POI:', {
          inspectionId,
          poiId,
          error: error instanceof Error ? error.message : error
        });
        dispatch(setInspection(null));
      } finally {
        setLoadingState(prev => ({ ...prev, poi: false }));
      }
    };

    initPOI();

    return () => {
      dispatch(setInspection(null));
      dispatch(setSelectedPOI(null));
      dispatch(setStepIndex(0));
    };

  }, [dispatch, ensureInspectionCached, inspectionId, inspections.length, poiId, project, stepIndex]);

  useEffect(() => {
    if (!project || !inspections.length || !inspectionId) return;

    setExpandedInspections(prev => {
      if (prev.has(inspectionId)) return prev;
      return new Set([...prev, inspectionId]);
    });
  }, [inspectionId, inspections.length, project]);

  const toggleExpand = useCallback((inspectionId: string) => {
    setExpandedInspections(prev => {
      const next = new Set(prev);
      if (next.has(inspectionId)) {
        next.delete(inspectionId);
      } else {
        next.add(inspectionId);
      }
      return next;
    });
  }, []);

  const handlePoiClick = useCallback(
    async (id: string, poi: Inspection.PointOfInterest) => {

      setLoadingState(prev => ({ ...prev, inspection: true }));

      try {
        const sp = await ensureInspectionCached(id);
        dispatch(setInspection(sp));
        navigate(
          generatePath(ROUTE_PATHS.PROJECT_INSPECTION_POI_STEP, {
            projectId,
            inspectionId: id,
            poiId: poi.id,
            stepIndex: String(poi.stepIndex ?? 0),
          })
        );
      } catch {
        dispatch(setInspection(null));
      } finally {
        setLoadingState(prev => ({ ...prev, inspection: false }));
      }
    },
    [dispatch, ensureInspectionCached, navigate, projectId]
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const pagedInspections = useMemo(() => {
    const filtered = inspections.filter(i =>
      !query ||
      i.info.name.toLowerCase().includes(query) ||
      i.pointOfInterests?.some(
        p =>
          p.name.toLowerCase().includes(query) ||
          (p.description ?? '').toLowerCase().includes(query)
      )
    );

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const data = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    return { filtered, totalPages, data };
  }, [inspections, query, page]);

  useEffect(() => {
    const isNotReady = !project || isLoading;
    (isNotReady ? show : hide)(componentId);
  }, [componentId, hide, show, isLoading, project]);

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Paper component="form" sx={{ p: '2px 4px', mb: 2, display: 'flex', alignItems: 'center', width: 400 }}>
          <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder={t('Project_Inspections_Search')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Paper>

        {!isLoading && pagedInspections.data.map(inspection => (
          <InspectionAccordion
            key={inspection.info.id}
            inspection={inspection}
            isExpanded={expandedInspections.has(inspection.info.id)}
            onToggleExpand={toggleExpand}
            onPoiClick={handlePoiClick}
            projectId={projectId}
            projectName={project?.projectInfo.name ?? '-'}
          />
        ))}

        {!isLoading && pagedInspections.data.length === 0 && (
          <Card sx={{ border: 'none' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchTerm ? t('Inspections_NotMatch') : t('Inspections_NotFound')}
              </Typography>
            </CardContent>
          </Card>
        )}

        {pagedInspections.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={pagedInspections.totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Box>
      <InspectionDetailModal />
    </>
  );
};

export default InspectionsTab;
